import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { WelcomeConfetti } from '../components/WelcomeConfetti';
import {
  ArrowRight, AlertCircle, Lock, Phone, User,
  Building2, CheckCircle2, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function InvitationSignup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { signupFromInvitation } = useAuth();

  const [status, setStatus] = useState('loading'); // loading, valid, invalid, success
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [userData, setUserData] = useState(null);

  const [formData, setFormData] = useState({
    password: '',
    password_confirm: '',
    phone: ''
  });

  useEffect(() => {
    const verifyInvitation = async () => {
      if (!token) {
        setStatus('invalid');
        return;
      }

      try {
        const { data } = await axios.get(`${API_URL}/api/invitations/verify/${token}`);
        setInvitation(data);
        setStatus('valid');
      } catch (err) {
        setStatus('invalid');
        setError(err.response?.data?.detail || 'Invalid or expired invitation');
      }
    };

    verifyInvitation();
  }, [token]);

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.password_confirm) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    const result = await signupFromInvitation(token, formData.password, formData.phone);

    if (result.success) {
      setUserData(result.user);
      setShowConfetti(true);
      setStatus('success');
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  };

  const getDashboardPath = (role) => {
    const paths = {
      ceo: '/dashboard/ceo',
      caregiver: '/dashboard/caregiver',
      client: '/dashboard/client',
      family: '/dashboard/family'
    };
    return paths[role] || '/';
  };

  const getRoleLabel = (role) => {
    const labels = {
      ceo: 'Principal Admin',
      caregiver: 'Caregiver',
      client: 'Client',
      family: 'Family Member'
    };
    return labels[role] || role;
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-[#FF6F61] animate-spin" />
          <p className="text-muted-foreground">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
            Invalid Invitation
          </h1>
          <p className="text-muted-foreground mb-8">
            {error || 'This invitation link is invalid or has expired.'}
          </p>
          <Link to="/">
            <Button className="btn-primary" data-testid="return-home-btn">
              Return Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <WelcomeConfetti show={showConfetti} />
        
        <div className="max-w-md w-full text-center welcome-animation">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#A8E6CF]/20 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#A8E6CF]" />
          </div>
          
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-4">
            Congratulations on your successful registration!
          </h1>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png" alt="FendanaCura" className="h-6 w-auto" />
            <span className="text-xl font-heading font-semibold text-[#FF6F61]">
              Welcome to FendanaCura
            </span>
          </div>
          
          <p className="text-lg text-muted-foreground italic mb-8">
            Smart care, seamlessly managed
          </p>

          <div className="p-6 rounded-xl bg-card border border-border text-left space-y-3 mb-8">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Role:</span>
              <span className="text-foreground font-medium">{getRoleLabel(userData?.role)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Your USIC:</span>
              <span className="text-[#FF6F61] font-mono text-sm">{userData?.usic}</span>
            </div>
            {userData?.staff_number && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Staff Number:</span>
                <span className="text-foreground font-mono text-sm">{userData?.staff_number}</span>
              </div>
            )}
          </div>

          <Button
            onClick={() => navigate(getDashboardPath(userData?.role))}
            className="btn-primary w-full py-6"
            data-testid="continue-to-dashboard-btn"
          >
            Continue to Dashboard
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png" alt="FendanaCura" className="h-10 w-auto" />
            <span className="text-xl font-heading font-bold text-foreground">FendanaCura</span>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">
            Complete Your Registration
          </h1>
          <p className="text-muted-foreground">
            You've been invited to join as {getRoleLabel(invitation?.role)}
          </p>
        </div>

        {/* Invitation Details */}
        <div className="p-4 rounded-xl bg-card border border-border mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#FF6F61]/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#FF6F61]" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Organization</p>
              <p className="text-foreground font-medium">{invitation?.organization_name}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Name</p>
              <p className="text-foreground">{invitation?.name}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Email</p>
              <p className="text-foreground">{invitation?.email}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Role</p>
              <p className="text-[#FF6F61]">{getRoleLabel(invitation?.role)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Your USIC</p>
              <p className="text-foreground font-mono text-xs">{invitation?.usic}</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Signup Form */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-foreground">Phone Number (Optional)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateForm('phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="bg-muted border-border text-foreground placeholder:text-muted-foreground pl-10"
                  data-testid="invite-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">Create Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateForm('password', e.target.value)}
                  placeholder="Minimum 6 characters"
                  required
                  className="bg-muted border-border text-foreground placeholder:text-muted-foreground pl-10"
                  data-testid="invite-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password_confirm" className="text-foreground">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password_confirm"
                  type="password"
                  value={formData.password_confirm}
                  onChange={(e) => updateForm('password_confirm', e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className="bg-muted border-border text-foreground placeholder:text-muted-foreground pl-10"
                  data-testid="invite-password-confirm-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-6 mt-6"
              data-testid="complete-signup-btn"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Account...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Complete Registration
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default InvitationSignup;
